import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAllEvents } from 'src/shared/services/eventService';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Grid, Paper } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useTranslation } from 'react-i18next';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Event } from '../models/event';
import { EventDateFilter, EventSearchFilter, EventSearchState } from '../models/eventSearchFilter';
import { EventListCalendar } from '../components/eventListCalendar';
import { EventCalendarInfoDots } from '../components/eventCalendarInfoDots';

export const EventMeCalendarPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(true);
  const [event, setEvent] = useState<Event[]>([]);

  const [eventSearchFilter] = useLocalStorage<EventSearchState>(LocalStorageKeys.EventSearchFilter, {
    districtIds: undefined,
    searchString: '',
  });
  const [eventDateFilter, setEventDateFilter] = useLocalStorage<EventDateFilter>(LocalStorageKeys.EventDateMemberMeFilterCalendar, {
    startDate: undefined,
    endDate: undefined,
  });

  const memoizedSearchFilter: EventSearchFilter = useMemo(() => {
    return {
      associationIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => !x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      districtIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      searchString: eventSearchFilter?.searchString,
    };
  }, [eventSearchFilter.districtIds, eventSearchFilter?.searchString]);

  const memoizedDateFilter: EventDateFilter = useMemo(() => {
    return {
      startDate: eventDateFilter?.startDate,
      endDate: eventDateFilter?.endDate,
    };
  }, [eventDateFilter.endDate, eventDateFilter.startDate]);

  const fetchData = useCallback(async () => {
    try {
      const res = await getAllEvents(0, 1000, 'date', 'asc', memoizedSearchFilter, memoizedDateFilter);
      if (isMounted()) setEvent(res.data.results);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, memoizedSearchFilter, memoizedDateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <GridWrapper>
        <Grid item xs={12}>
          <MainContentHeader title={t('event:events')} />
        </Grid>
        <Grid item xs={12} sx={{ mb: 0 }} />
      </GridWrapper>
      <GridWrapper>
        <Grid item xs={12}>
          <Paper sx={{ pt: 1 }}>
            <EventCalendarInfoDots />
            <EventListCalendar
              loading={false}
              data={event}
              initialDate={eventDateFilter?.startDate ? DateHelper.getDate(eventDateFilter?.startDate) : undefined}
              dateCallback={(from: string, to: string) =>
                setEventDateFilter((prev) => {
                  if (prev.startDate !== from) {
                    return { ...prev, startDate: from || undefined, endDate: to || undefined };
                  }
                  return prev;
                })
              }
            />
          </Paper>
        </Grid>
      </GridWrapper>
    </>
  );
};
